import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Facebook, Instagram, Globe, Loader2, CheckCircle, AlertCircle, Circle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePostHogTracking } from '@/hooks/usePostHogTracking';
import { toast } from 'sonner';
import { Link } from '@/components/Link';
import { startScrapeJob, getScrapeJob, ScrapeJob, ScrapeJobStatus, ToolCallLog, StoreAlreadyExistsInfo } from '@/api/ecommerceScrapeService';

interface AddStoreModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Curated user-facing phases (internal tool names are never shown).
const PHASES = [
  'جاري قراءة صفحة المتجر…',
  'جاري تحليل وتنظيم البيانات…',
  'جاري تصنيف وإثراء المعلومات…',
  'جاري تجهيز صفحة المتجر…',
] as const;

// Maps known internal tool names to a curated phase index.
// Any tool NOT in this map is ignored (never surfaced to the user).
// Phase 1 deliberately absorbs all hidden normalization/merge/eligibility steps.
const PHASE_MAP: Record<string, number> = {
  scrape_facebook_page: 0,
  scrape_instagram_profile: 0,
  scrape_website_full: 0,
  scrape_single_page: 0,
  _llm_call: 1,
  _normalize_website: 1,
  _merge_store_data: 1,
  merge_all_data: 1,
  store_eligibility_check: 1,
  enrich_infer_category: 2,
  enrich_google_places: 2,
  enrich_discover_metadata: 2,
  enrich_structure_products: 2,
  enrich_seo_content: 2,
  enrich_calculate_rating: 2,
  enrich_import_google_reviews: 2,
  enrich_import_facebook_reviews: 2,
  final_store_output: 3,
  force_create_store: 3,
  merge_with_existing_store: 3,
};

type PhaseStatus = 'pending' | 'active' | 'done';

const TERMINAL_STATUSES: ScrapeJobStatus[] = ['completed', 'failed', 'partial'];

const isTerminalStatus = (status: ScrapeJobStatus): boolean =>
  TERMINAL_STATUSES.includes(status);

// Derives the 4 curated phase statuses from real job polling data.
const computePhaseStatuses = (
  toolCalls: ToolCallLog[] = [],
  status: ScrapeJobStatus,
): PhaseStatus[] => {
  // On terminal success/partial, every phase is complete.
  if (status === 'completed' || status === 'partial') {
    return ['done', 'done', 'done', 'done'];
  }

  const done = [false, false, false, false];
  for (const tc of toolCalls) {
    const idx = PHASE_MAP[tc.toolName];
    if (idx === undefined) continue;
    if (tc.completedAt && !tc.error) {
      done[idx] = true;
    }
  }

  const result: PhaseStatus[] = ['pending', 'pending', 'pending', 'pending'];
  let activeAssigned = false;
  for (let i = 0; i < 4; i++) {
    if (done[i]) {
      result[i] = 'done';
    } else if (!activeAssigned && (status === 'pending' || status === 'processing')) {
      result[i] = 'active';
      activeAssigned = true;
    }
  }
  return result;
};

export const AddStoreModal: React.FC<AddStoreModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { trackStore } = usePostHogTracking();
  const [url, setUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<ScrapeJob | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Set when the backend rejects the URL because we already have that store (409),
  // so the user gets a way through to it instead of a dead-end error.
  const [duplicateStore, setDuplicateStore] = useState<StoreAlreadyExistsInfo | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearPoll = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Poll the job while a jobId is set and the job is not terminal.
  useEffect(() => {
    if (!jobId) return;
    if (typeof window === 'undefined') return;

    let cancelled = false;

    const poll = async () => {
      const res = await getScrapeJob(jobId);
      if (cancelled) return;
      if (res.ok) {
        setJob(res.data);
        if (isTerminalStatus(res.data.status)) {
          clearPoll();
          const terminalStoreId = res.data.draftStoreId ?? res.data.existingStoreId;
          trackStore.submissionCompleted({
            success: res.data.status !== 'failed',
            ...(terminalStoreId ? { store_id: String(terminalStoreId) } : {}),
          });
        }
      }
    };

    // Immediate first fetch so the tracker doesn't sit empty for 2s.
    poll();
    intervalRef.current = setInterval(poll, 2000);

    return () => {
      cancelled = true;
      clearPoll();
    };
  }, [jobId, trackStore]);

  // Accepts links with or without a scheme (e.g. "example.com"); returns a
  // scheme-prefixed URL the backend's `new URL()` check will accept, or null.
  const normalizeUrl = ({ url }: { url: string }): string | null => {
    const withScheme = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    try {
      const parsed = new URL(withScheme);
      if (!parsed.hostname.includes('.')) return null;
      return withScheme;
    } catch {
      return null;
    }
  };

  const detectPlatform = ({ url }: { url: string }): string => {
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('facebook.com') || lowerUrl.includes('fb.com')) {
      return 'facebook';
    }
    if (lowerUrl.includes('instagram.com')) {
      return 'instagram';
    }
    return 'website';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast.error("خطأ في المصادقة", { description: "يجب تسجيل الدخول أولاً" });
      return;
    }

    if (!url.trim()) {
      toast.error("رابط مطلوب", { description: "يرجى إدخال رابط صفحة المتجر" });
      return;
    }

    const normalizedUrl = normalizeUrl({ url: url.trim() });
    if (!normalizedUrl) {
      toast.error("رابط غير صحيح", { description: "يرجى إدخال رابط صحيح مثل example.com" });
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setDuplicateStore(null);

    // Hostname only — never the full pasted URL (may carry query params).
    let urlDomain: string;
    try {
      urlDomain = new URL(normalizedUrl).hostname;
    } catch {
      urlDomain = url.trim();
    }
    trackStore.submitted({ url_domain: urlDomain });

    try {
      const res = await startScrapeJob({ url: normalizedUrl });

      if (res.ok) {
        const newJobId = (res.data as any).jobId as string | undefined;
        if (!newJobId) {
          setSubmitError('تعذّر بدء معالجة المتجر، يرجى المحاولة مرة أخرى');
          toast.error("خطأ", { description: 'تعذّر بدء معالجة المتجر' });
          return;
        }
        setJob(null);
        setJobId(newJobId);
      } else {
        const alreadyExists = /exist|موجود|already/i.test(res.error || '');
        const message = alreadyExists
          ? 'هذا المتجر موجود بالفعل ضمن دليلنا'
          : (res.error || 'حدث خطأ أثناء معالجة الطلب');
        setSubmitError(message);
        if (alreadyExists && res.existingStore?.storeId) {
          setDuplicateStore(res.existingStore);
        }
        toast.error("خطأ في الإضافة", { description: message });
      }
    } catch (error) {
      console.error('Error submitting store:', error);
      setSubmitError('حدث خطأ في الاتصال بالخادم');
      toast.error("خطأ في الاتصال", { description: 'حدث خطأ في الاتصال بالخادم' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    clearPoll();
    setUrl('');
    setJobId(null);
    setJob(null);
    setSubmitError(null);
    setDuplicateStore(null);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  const platform = url ? detectPlatform({ url }) : '';

  const status: ScrapeJobStatus | null = job?.status ?? null;
  const isTerminal = status ? isTerminalStatus(status) : false;
  const phaseStatuses: PhaseStatus[] = computePhaseStatuses(
    job?.toolCalls ?? [],
    status ?? 'pending',
  );
  const storeId = job?.draftStoreId ?? job?.existingStoreId;

  const renderTracker = () => (
    <div className="py-4">
      <p className="text-sm text-gray-600 mb-4 text-center">
        نعمل الآن على تجهيز صفحة المتجر، قد يستغرق ذلك بضع دقائق…
      </p>
      <ul className="space-y-3">
        {PHASES.map((label, i) => {
          const st = phaseStatuses[i];
          return (
            <li key={i} className="flex items-center gap-3">
              {st === 'done' ? (
                <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
              ) : st === 'active' ? (
                <Loader2 className="w-5 h-5 text-warmOrange-500 animate-spin shrink-0" />
              ) : (
                <Circle className="w-5 h-5 text-gray-300 shrink-0" />
              )}
              <span
                className={
                  st === 'done'
                    ? 'text-sm text-gray-800'
                    : st === 'active'
                    ? 'text-sm font-medium text-gray-900'
                    : 'text-sm text-gray-400'
                }
              >
                {label}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );

  const renderTerminal = () => {
    if (status === 'failed') {
      return (
        <div className="py-6 text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-3 text-red-600" />
          <p className="text-lg font-medium mb-2">تعذّر إضافة المتجر</p>
          <p className="text-sm text-gray-600 mb-4">
            {job?.error || 'حدث خطأ أثناء معالجة المتجر، يرجى المحاولة مرة أخرى'}
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={resetForm} variant="outline">
              إضافة متجر آخر
            </Button>
            <Button onClick={handleClose}>إغلاق</Button>
          </div>
        </div>
      );
    }

    // completed or partial
    const isPartial = status === 'partial';
    return (
      <div className="py-6 text-center">
        <CheckCircle className="w-16 h-16 mx-auto mb-3 text-green-600" />
        <p className="text-lg font-medium mb-2">
          {isPartial ? 'تم إضافة المتجر بنجاح جزئي 🎉' : 'تم إضافة المتجر بنجاح! 🎉'}
        </p>
        {isPartial && (
          <p className="text-sm text-gray-600 mb-2">
            تمت إضافة المتجر مع بعض البيانات، وسنكمل بقية التفاصيل لاحقاً.
          </p>
        )}
        <p className="text-sm text-gray-600 mb-4">شكراً لمشاركتك متجراً جديداً مع المجتمع</p>

        {storeId && (
          <Link to={`/store/${storeId}`} onClick={handleClose}>
            <Button className="mb-4 bg-warmOrange-500 hover:bg-warmOrange-600">
              عرض المتجر
            </Button>
          </Link>
        )}

        <div className="flex gap-3 justify-center">
          <Button onClick={resetForm} variant="outline">
            إضافة متجر آخر
          </Button>
          <Button onClick={handleClose}>إغلاق</Button>
        </div>
      </div>
    );
  };

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="store-url" className="text-sm font-medium text-gray-700">
          رابط المتجر (موقع إلكتروني، فيسبوك، أو انستجرام)
        </Label>
        <div className="relative">
          <Input
            id="store-url"
            type="text"
            inputMode="url"
            placeholder="example.com أو رابط فيسبوك/انستجرام"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="pr-10"
            disabled={isSubmitting}
            dir="ltr"
          />
          {platform && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {platform === 'facebook' ? (
                <Facebook className="w-4 h-4 text-blue-600" />
              ) : platform === 'instagram' ? (
                <Instagram className="w-4 h-4 text-pink-600" />
              ) : (
                <Globe className="w-4 h-4 text-gray-500" />
              )}
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500">
          سيتم استخراج معلومات المتجر تلقائياً من الرابط
        </p>
      </div>

      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="space-y-2">
            <span>{submitError}</span>
            {duplicateStore?.storeId && (
              <div>
                <Link
                  to={`/store/${duplicateStore.slug || duplicateStore.storeId}`}
                  onClick={() => {
                    trackStore.duplicateViewClicked({
                      store_id: duplicateStore.storeId,
                      slug: duplicateStore.slug,
                    });
                    handleClose();
                  }}
                  className="font-medium text-warmOrange-600 underline underline-offset-4 hover:text-warmOrange-700"
                >
                  عرض المتجر
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <h4 className="font-medium mb-2">💡 نصائح مفيدة:</h4>
        <ul className="space-y-1 text-xs">
          <li>• يمكنك مشاركة متاجر المواقع الإلكترونية وصفحات فيسبوك وانستجرام</li>
          <li>• تأكد من أن الصفحة عامة وقابلة للوصول</li>
          <li>• يفضل استخدام الرابط الرئيسي للمتجر</li>
        </ul>
      </div>

      <div className="flex gap-3 justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          إلغاء
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting || !url.trim()}
          className="bg-warmOrange-500 hover:bg-warmOrange-600"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 ml-2 animate-spin" />
              جاري المعالجة...
            </>
          ) : (
            'إضافة المتجر'
          )}
        </Button>
      </div>
    </form>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md font-arabic" dir="rtl">
        <DialogHeader className="text-right sm:text-right">
          <DialogTitle className="text-xl font-bold text-gray-900">
            شارك متجر جديد
          </DialogTitle>
        </DialogHeader>

        {isTerminal ? renderTerminal() : jobId ? renderTracker() : renderForm()}
      </DialogContent>
    </Dialog>
  );
};
